import { useEffect, useRef, useState } from "react";
import { Crop, ZoomIn, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RATIOS = [
  { key: "wide", label: "1,91:1 (consigliato)", ratio: 1.91, out: [1125, 600] },
  { key: "square", label: "1:1 quadrato", ratio: 1, out: [1080, 1080] },
];
const VIEW_W = 520;
const FONT = "'Plus Jakarta Sans', Arial, sans-serif";
const BADGE_COLORS = { rosso: ["#DC2626", "#FFFFFF"], giallo: ["#FACC15", "#1F2937"], verde: ["#059669", "#FFFFFF"], blu: ["#2563EB", "#FFFFFF"] };
const BADGE_POS = { "alto-dx": "In alto a destra", "alto-sx": "In alto a sinistra", "basso-dx": "In basso a destra" };
const LOGO_POS = { "basso-dx": "In basso a destra", "basso-sx": "In basso a sinistra", "alto-dx": "In alto a destra", "alto-sx": "In alto a sinistra" };

function drawRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

function wrapLines(ctx, text, maxWidth, maxLines) {
  const words = text.split(/\s+/).filter(Boolean); const lines = []; let cur = "";
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width <= maxWidth || !cur) cur = t; else { lines.push(cur); cur = w; }
    if (lines.length === maxLines) break;
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  if (lines.length === maxLines && ctx.measureText(lines[maxLines - 1]).width > maxWidth) lines[maxLines - 1] = lines[maxLines - 1].slice(0, -1) + "…";
  return lines;
}

// Disegna titolo e badge sconto; stessa funzione per anteprima (viewport) ed export (canvas finale), scala in base a W.
export function drawOverlay(ctx, W, H, { title, titlePos, titleBand, badge, badgeColor, badgePos, logo, logoOn, logoPos = "basso-dx", logoSize = "medio", logoPlate = true }) {
  const pad = W * 0.04;
  if (title?.trim()) {
    const fs = Math.round(W * 0.07);
    ctx.font = `800 ${fs}px ${FONT}`;
    // se il logo occupa lo stesso lato del titolo, il titolo si restringe/si sposta per non sovrapporsi
    const logoActive = logoOn && logo?.naturalWidth;
    const logoW = logoActive ? W * (logoSize === "piccolo" ? 0.13 : 0.19) + W * 0.06 : 0;
    const logoSameSide = logoActive && (logoPos.startsWith("alto") ? titlePos === "alto" : titlePos !== "alto");
    const shiftX = logoSameSide && logoPos.endsWith("sx") ? logoW : 0;
    const maxW = W - pad * 2 - (badge && badgePos.startsWith("alto") === (titlePos === "alto") ? W * 0.24 : 0) - (logoSameSide ? logoW : 0);
    const lines = wrapLines(ctx, title.trim(), maxW, 2);
    const bandH = lines.length * fs * 1.15 + pad * 2;
    if (titleBand) {
      const top = titlePos === "alto";
      const g = ctx.createLinearGradient(0, top ? 0 : H - bandH * 1.4, 0, top ? bandH * 1.4 : H);
      g.addColorStop(top ? 1 : 0, "rgba(0,0,0,0)"); g.addColorStop(top ? 0 : 1, "rgba(0,0,0,0.78)");
      ctx.fillStyle = g; ctx.fillRect(0, top ? 0 : H - bandH * 1.4, W, bandH * 1.4);
    }
    ctx.fillStyle = "#FFFFFF"; ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
    ctx.shadowColor = "rgba(0,0,0,0.6)"; ctx.shadowBlur = fs * 0.25; ctx.shadowOffsetY = fs * 0.05;
    lines.forEach((l, i) => ctx.fillText(l, pad + shiftX, (titlePos === "alto" ? pad + fs : H - pad - (lines.length - 1 - i) * fs * 1.15) + (titlePos === "alto" ? i * fs * 1.15 : 0)));
    ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
  }
  if (badge?.trim()) {
    const d = W * 0.22, r = d / 2, m = W * 0.035;
    const cx = badgePos.endsWith("sx") ? m + r : W - m - r, cy = badgePos.startsWith("alto") ? m + r : H - m - r;
    const [bg, fg] = BADGE_COLORS[badgeColor] || BADGE_COLORS.rosso;
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(-0.14);
    ctx.shadowColor = "rgba(0,0,0,0.35)"; ctx.shadowBlur = d * 0.12; ctx.shadowOffsetY = d * 0.04;
    ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fillStyle = bg; ctx.fill();
    ctx.shadowColor = "transparent"; ctx.lineWidth = d * 0.035; ctx.strokeStyle = "rgba(255,255,255,0.9)"; ctx.beginPath(); ctx.arc(0, 0, r * 0.86, 0, Math.PI * 2); ctx.stroke();
    const txt = badge.trim(); const fs = txt.length <= 4 ? d * 0.36 : txt.length <= 7 ? d * 0.26 : d * 0.2;
    ctx.fillStyle = fg; ctx.font = `800 ${fs}px ${FONT}`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(txt, 0, fs * 0.05);
    ctx.restore();
  }
  if (logoOn && logo?.naturalWidth) {  // disegnato per ultimo: resta sopra banda e titolo
    const lw = W * (logoSize === "piccolo" ? 0.13 : 0.19), lh = lw * logo.naturalHeight / logo.naturalWidth, m = W * 0.03, p = W * 0.012;
    const x = logoPos.endsWith("sx") ? m : W - m - lw, y = logoPos.startsWith("alto") ? m : H - m - lh;
    ctx.save();
    if (logoPlate) { ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.shadowColor = "rgba(0,0,0,0.3)"; ctx.shadowBlur = W * 0.01; drawRoundRect(ctx, x - p, y - p, lw + p * 2, lh + p * 2, W * 0.012); ctx.fill(); ctx.shadowColor = "transparent"; }
    ctx.drawImage(logo, x, y, lw, lh);
    ctx.restore();
  }
}

// Ritaglio guidato: l'immagine si sposta/ingrandisce dentro una finestra con proporzioni fisse; l'export avviene via canvas.
export function ImageCropDialog({ file, open, onCancel, onConfirm, logoUrl, onLogoUpload }) {
  const [ratioKey, setRatioKey] = useState("wide");
  const [img, setImg] = useState(null);
  const [logo, setLogo] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [ov, setOv] = useState({ title: "", titlePos: "basso", titleBand: true, badge: "", badgeColor: "rosso", badgePos: "alto-dx", logoOn: !!logoUrl, logoPos: "basso-dx", logoSize: "medio", logoPlate: true });
  const setO = (patch) => setOv((o) => ({ ...o, ...patch }));
  const drag = useRef(null);
  const overlayRef = useRef(null);
  const R = RATIOS.find((r) => r.key === ratioKey);
  const viewW = VIEW_W, viewH = Math.round(VIEW_W / R.ratio);

  useEffect(() => {
    if (!logoUrl) { setLogo(null); return; }
    const l = new Image(); l.crossOrigin = "anonymous";
    l.onload = () => { setLogo(l); setO({ logoOn: true }); };
    l.onerror = () => setLogo(null);
    l.src = logoUrl;
  }, [logoUrl]);
  const ovAll = { ...ov, logo };

  useEffect(() => {
    const c = overlayRef.current; if (!c) return;
    const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height);
    document.fonts?.load?.(`800 20px ${FONT}`).finally(() => { ctx.clearRect(0, 0, c.width, c.height); drawOverlay(ctx, viewW, viewH, { ...ov, logo }); });
  }, [ov, logo, viewW, viewH, img]);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => { setImg(i); setZoom(1); setPos({ x: 0, y: 0 }); };
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => { setZoom(1); setPos({ x: 0, y: 0 }); }, [ratioKey]);

  if (!img) return null;
  const base = Math.max(viewW / img.naturalWidth, viewH / img.naturalHeight); // "cover"
  const scale = base * zoom;
  const drawW = img.naturalWidth * scale, drawH = img.naturalHeight * scale;
  const clamp = (p) => ({ x: Math.min(0, Math.max(viewW - drawW, p.x)), y: Math.min(0, Math.max(viewH - drawH, p.y)) });
  const centered = clamp({ x: (viewW - drawW) / 2 + pos.x, y: (viewH - drawH) / 2 + pos.y });

  const onDown = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const onMove = (e) => { if (!drag.current) return; setPos({ x: drag.current.ox + (e.clientX - drag.current.sx), y: drag.current.oy + (e.clientY - drag.current.sy) }); };
  const onUp = () => { drag.current = null; };

  const confirm = () => {
    const [outW, outH] = R.out;
    const canvas = document.createElement("canvas");
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext("2d");
    const k = outW / viewW;
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, centered.x * k, centered.y * k, drawW * k, drawH * k);
    drawOverlay(ctx, outW, outH, ovAll);
    canvas.toBlob((blob) => onConfirm(new File([blob], (file.name || "immagine").replace(/\.[^.]+$/, "") + `_${outW}x${outH}.jpg`, { type: "image/jpeg" }),
      { width: outW, height: outH, size: blob.size, type: "image/jpeg" }), "image/jpeg", 0.86);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="image-crop-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Crop className="h-5 w-5 text-[#10B981]" /> Ritaglia l'immagine per WhatsApp</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2" data-testid="crop-ratio-options">
          {RATIOS.map((r) => (
            <button key={r.key} type="button" onClick={() => setRatioKey(r.key)} data-testid={`crop-ratio-${r.key}`}
                    className={`rounded-full px-3 py-1 text-xs border ${ratioKey === r.key ? "bg-[#10B981] text-slate-950 border-[#10B981]" : "border-[#1F2937] text-slate-300 hover:border-slate-500"}`}>{r.label} · {r.out[0]}×{r.out[1]} px</button>
          ))}
        </div>
        <div className="mx-auto relative select-none touch-none overflow-hidden rounded-lg border-2 border-[#10B981] bg-black cursor-grab active:cursor-grabbing" style={{ width: viewW, height: viewH, maxWidth: "100%" }}
             onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-testid="crop-viewport">
          <img src={img.src} alt="" draggable={false} style={{ position: "absolute", left: centered.x, top: centered.y, width: drawW, height: drawH, maxWidth: "none" }} />
          <canvas ref={overlayRef} width={viewW} height={viewH} className="absolute inset-0 pointer-events-none" data-testid="crop-overlay" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="crop-overlay-controls">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-800">Titolo sull'immagine <span className="text-slate-500 font-normal">(opzionale)</span></label>
            <input value={ov.title} maxLength={60} onChange={(e) => setO({ title: e.target.value })} placeholder="Es. OFFERTE DEL WEEKEND" className="w-full rounded-md bg-white border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400" data-testid="crop-title" />
            <div className="flex flex-wrap gap-3 text-[11px] text-slate-700">
              <label className="flex items-center gap-1"><input type="radio" checked={ov.titlePos === "basso"} onChange={() => setO({ titlePos: "basso" })} className="accent-[#10B981]" data-testid="crop-title-basso" /> In basso</label>
              <label className="flex items-center gap-1"><input type="radio" checked={ov.titlePos === "alto"} onChange={() => setO({ titlePos: "alto" })} className="accent-[#10B981]" data-testid="crop-title-alto" /> In alto</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={ov.titleBand} onChange={(e) => setO({ titleBand: e.target.checked })} className="accent-[#10B981]" data-testid="crop-title-band" /> Banda scura per leggibilità</label>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-800">Badge sconto <span className="text-slate-500 font-normal">(opzionale)</span></label>
            <div className="flex gap-2">
              <input value={ov.badge} maxLength={10} onChange={(e) => setO({ badge: e.target.value })} placeholder="-30%" className="w-24 rounded-md bg-white border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400" data-testid="crop-badge" />
              <select value={ov.badgeColor} onChange={(e) => setO({ badgeColor: e.target.value })} className="flex-1 rounded-md bg-white border border-slate-300 px-2 py-1.5 text-xs text-slate-900" data-testid="crop-badge-color">
                {Object.keys(BADGE_COLORS).map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
              </select>
              <select value={ov.badgePos} onChange={(e) => setO({ badgePos: e.target.value })} className="flex-1 rounded-md bg-white border border-slate-300 px-2 py-1.5 text-xs text-slate-900" data-testid="crop-badge-pos">
                {Object.entries(BADGE_POS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <p className="text-[11px] text-slate-500">Esempi: -30%, 2x1, NOVITÀ, SOTTOCOSTO. Il testo deve corrispondere all'offerta nel messaggio (regola Meta).</p>
          </div>
          <div className="sm:col-span-2 space-y-1.5 border-t border-slate-200 pt-3" data-testid="crop-logo-controls">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                <input type="checkbox" checked={ov.logoOn && !!logo} disabled={!logo} onChange={(e) => setO({ logoOn: e.target.checked })} className="accent-[#10B981]" data-testid="crop-logo-on" /> Logo insegna sull'immagine
              </label>
              {logoUrl && <img src={logoUrl} alt="Logo insegna" className="h-7 max-w-[120px] object-contain rounded bg-white px-1" data-testid="crop-logo-preview" />}
              {onLogoUpload && (
                <label className="text-[11px] text-[#10B981] hover:underline cursor-pointer" data-testid="crop-logo-upload">
                  {logoUrl ? "Cambia logo" : "Carica il logo dell'insegna (PNG con sfondo trasparente)"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onLogoUpload(f); }} />
                </label>
              )}
            </div>
            {logo && ov.logoOn && (
              <div className="flex flex-wrap gap-2">
                <select value={ov.logoPos} onChange={(e) => setO({ logoPos: e.target.value })} className="rounded-md bg-white border border-slate-300 px-2 py-1.5 text-xs text-slate-900" data-testid="crop-logo-pos">
                  {Object.entries(LOGO_POS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={ov.logoSize} onChange={(e) => setO({ logoSize: e.target.value })} className="rounded-md bg-white border border-slate-300 px-2 py-1.5 text-xs text-slate-900" data-testid="crop-logo-size">
                  <option value="piccolo">Piccolo</option><option value="medio">Medio</option>
                </select>
                <label className="flex items-center gap-1 text-[11px] text-slate-700"><input type="checkbox" checked={ov.logoPlate} onChange={(e) => setO({ logoPlate: e.target.checked })} className="accent-[#10B981]" data-testid="crop-logo-plate" /> Riquadro bianco dietro il logo</label>
              </div>
            )}
            <p className="text-[11px] text-slate-500">Il logo è salvato una volta per la tua attività (Impostazioni) e riutilizzato in tutti i volantini con un clic.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <ZoomIn className="h-4 w-4" />
          <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#10B981]" data-testid="crop-zoom" />
          <span className="w-10 text-right">{Math.round(zoom * 100)}%</span>
        </div>
        <p className="text-[11px] text-slate-500">Trascina l'immagine per inquadrare, usa lo zoom per avvicinare. L'area verde è ciò che vedrà il cliente; il file esportato è un JPG {R.out[0]}×{R.out[1]} px, sempre sotto i 5 MB.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="bg-[#1E293B] border-[#1F2937] text-slate-200" data-testid="crop-cancel">Annulla</Button>
          <Button type="button" onClick={confirm} className="bg-[#10B981] hover:bg-[#059669] text-slate-950 font-semibold" data-testid="crop-confirm"><Check className="h-4 w-4 mr-1" /> Ritaglia e carica</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
